import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Wrench, DollarSign, Sun, Moon, Clock, Truck } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Rates() {
  const { token } = useAuth();
  const [crafts, setCrafts] = useState([]);
  const [classifications, setClassifications] = useState([]);
  const [internalEquipment, setInternalEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Craft dialog state
  const [craftDialogOpen, setCraftDialogOpen] = useState(false);
  const [editingCraft, setEditingCraft] = useState(null);
  const [craftFormData, setCraftFormData] = useState({ name: '' });
  const [craftSubmitting, setCraftSubmitting] = useState(false);
  const [deleteCraftDialogOpen, setDeleteCraftDialogOpen] = useState(false);
  const [deletingCraft, setDeletingCraft] = useState(null);
  
  // Classification dialog state
  const [classDialogOpen, setClassDialogOpen] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [classFormData, setClassFormData] = useState({
    craftId: '',
    name: '',
    stRate: '',
    otRate: '',
    dtRate: '',
    shiftDiffType: 'MULTIPLIER', // 'MULTIPLIER' or 'FLAT'
    shiftDiffs: {
      DAY: 1.0,
      NIGHT: 1.0,
      SWING: 1.0
    },
    shiftDiffsFlat: {
      DAY: 0,
      NIGHT: 0,
      SWING: 0
    }
  });
  const [classSubmitting, setClassSubmitting] = useState(false);
  const [deleteClassDialogOpen, setDeleteClassDialogOpen] = useState(false);
  const [deletingClass, setDeletingClass] = useState(null);

  // Internal Equipment dialog state
  const [equipDialogOpen, setEquipDialogOpen] = useState(false);
  const [editingEquip, setEditingEquip] = useState(null);
  const [equipFormData, setEquipFormData] = useState({
    name: '',
    description: '',
    hourlyRate: ''
  });
  const [equipSubmitting, setEquipSubmitting] = useState(false);
  const [deleteEquipDialogOpen, setDeleteEquipDialogOpen] = useState(false);
  const [deletingEquip, setDeletingEquip] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [craftsRes, classRes, equipRes] = await Promise.all([
        axios.get(`${API}/crafts`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/classifications`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/internal-equipment`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setCrafts(craftsRes.data);
      setClassifications(classRes.data);
      setInternalEquipment(equipRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load rates data');
    } finally {
      setLoading(false);
    }
  };

  const getCraftName = (craftId) => {
    const craft = crafts.find(c => c.id === craftId);
    return craft ? craft.name : craftId;
  };

  // Format shift differential for display
  const formatShiftDiff = (classification, shift) => {
    const hasFlat = classification.shiftDiffsFlat && classification.shiftDiffsFlat[shift] > 0;
    if (hasFlat) {
      return `+$${classification.shiftDiffsFlat[shift].toFixed(2)}`;
    }
    const multiplier = classification.shiftDiffs?.[shift] || 1.0;
    return `${multiplier.toFixed(2)}x`;
  };

  // Craft handlers
  const handleOpenCraftDialog = (craft = null) => {
    if (craft) {
      setEditingCraft(craft);
      setCraftFormData({ name: craft.name });
    } else {
      setEditingCraft(null);
      setCraftFormData({ name: '' });
    }
    setCraftDialogOpen(true);
  };

  const handleCraftSubmit = async (e) => {
    e.preventDefault();
    setCraftSubmitting(true);

    try {
      if (editingCraft) {
        await axios.patch(`${API}/crafts/${editingCraft.id}`, craftFormData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Craft updated successfully');
      } else {
        await axios.post(`${API}/crafts`, craftFormData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Craft created successfully');
      }
      setCraftDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Failed to save craft:', error);
      toast.error(error.response?.data?.detail || 'Failed to save craft');
    } finally {
      setCraftSubmitting(false);
    }
  };

  const handleDeleteCraft = async () => {
    if (!deletingCraft) return;

    try {
      await axios.delete(`${API}/crafts/${deletingCraft.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Craft deleted successfully');
      setDeleteCraftDialogOpen(false);
      setDeletingCraft(null);
      fetchData();
    } catch (error) {
      console.error('Failed to delete craft:', error);
      toast.error('Failed to delete craft');
    }
  };

  // Classification handlers
  const handleOpenClassDialog = (classification = null) => {
    if (classification) {
      setEditingClass(classification);
      // Determine if using flat amounts (if shiftDiffsFlat exists and has non-zero values)
      const hasFlat = classification.shiftDiffsFlat && 
        (classification.shiftDiffsFlat.DAY > 0 || 
         classification.shiftDiffsFlat.NIGHT > 0 || 
         classification.shiftDiffsFlat.SWING > 0);
      
      setClassFormData({
        craftId: classification.craftId,
        name: classification.name,
        stRate: classification.stRate.toString(),
        otRate: classification.otRate.toString(),
        dtRate: classification.dtRate.toString(),
        shiftDiffType: hasFlat ? 'FLAT' : 'MULTIPLIER',
        shiftDiffs: classification.shiftDiffs || {
          DAY: 1.0,
          NIGHT: 1.0,
          SWING: 1.0
        },
        shiftDiffsFlat: classification.shiftDiffsFlat || {
          DAY: 0,
          NIGHT: 0,
          SWING: 0
        }
      });
    } else {
      setEditingClass(null);
      setClassFormData({
        craftId: '',
        name: '',
        stRate: '',
        otRate: '',
        dtRate: '',
        shiftDiffType: 'MULTIPLIER',
        shiftDiffs: {
          DAY: 1.0,
          NIGHT: 1.0,
          SWING: 1.0
        },
        shiftDiffsFlat: {
          DAY: 0,
          NIGHT: 0,
          SWING: 0
        }
      });
    }
    setClassDialogOpen(true);
  };

  const handleClassSubmit = async (e) => {
    e.preventDefault();
    setClassSubmitting(true);

    try {
      const payload = {
        ...classFormData,
        stRate: parseFloat(classFormData.stRate),
        otRate: parseFloat(classFormData.otRate),
        dtRate: parseFloat(classFormData.dtRate),
        shiftDiffType: classFormData.shiftDiffType,
        shiftDiffs: classFormData.shiftDiffType === 'MULTIPLIER' ? {
          DAY: parseFloat(classFormData.shiftDiffs?.DAY) || 1.0,
          NIGHT: parseFloat(classFormData.shiftDiffs?.NIGHT) || 1.0,
          SWING: parseFloat(classFormData.shiftDiffs?.SWING) || 1.0
        } : { DAY: 1.0, NIGHT: 1.0, SWING: 1.0 },
        shiftDiffsFlat: classFormData.shiftDiffType === 'FLAT' ? {
          DAY: parseFloat(classFormData.shiftDiffsFlat?.DAY) || 0,
          NIGHT: parseFloat(classFormData.shiftDiffsFlat?.NIGHT) || 0,
          SWING: parseFloat(classFormData.shiftDiffsFlat?.SWING) || 0
        } : { DAY: 0, NIGHT: 0, SWING: 0 }
      };

      if (editingClass) {
        await axios.patch(`${API}/classifications/${editingClass.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Classification updated successfully');
      } else {
        await axios.post(`${API}/classifications`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Classification created successfully');
      }
      setClassDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Failed to save classification:', error);
      toast.error(error.response?.data?.detail || 'Failed to save classification');
    } finally {
      setClassSubmitting(false);
    }
  };

  const handleDeleteClass = async () => {
    if (!deletingClass) return;

    try {
      await axios.delete(`${API}/classifications/${deletingClass.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Classification deleted successfully');
      setDeleteClassDialogOpen(false);
      setDeletingClass(null);
      fetchData();
    } catch (error) {
      console.error('Failed to delete classification:', error);
      toast.error('Failed to delete classification');
    }
  };

  // Internal Equipment handlers
  const openEquipDialog = (equip = null) => {
    if (equip) {
      setEditingEquip(equip);
      setEquipFormData({
        name: equip.name,
        description: equip.description || '',
        hourlyRate: equip.hourlyRate.toString()
      });
    } else {
      setEditingEquip(null);
      setEquipFormData({
        name: '',
        description: '',
        hourlyRate: ''
      });
    }
    setEquipDialogOpen(true);
  };

  const handleEquipSubmit = async (e) => {
    e.preventDefault();
    setEquipSubmitting(true);

    try {
      const payload = {
        name: equipFormData.name,
        description: equipFormData.description || null,
        hourlyRate: parseFloat(equipFormData.hourlyRate)
      };

      if (editingEquip) {
        await axios.put(`${API}/internal-equipment/${editingEquip.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Equipment updated successfully');
      } else {
        await axios.post(`${API}/internal-equipment`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Equipment created successfully');
      }
      setEquipDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Failed to save equipment:', error);
      toast.error(error.response?.data?.detail || 'Failed to save equipment');
    } finally {
      setEquipSubmitting(false);
    }
  };

  const handleDeleteEquip = async () => {
    if (!deletingEquip) return;

    try {
      await axios.delete(`${API}/internal-equipment/${deletingEquip.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Equipment deleted successfully');
      setDeleteEquipDialogOpen(false);
      setDeletingEquip(null);
      fetchData();
    } catch (error) {
      console.error('Failed to delete equipment:', error);
      toast.error('Failed to delete equipment');
    }
  };

  if (loading) {
    return (
      <div className="p-4 md:p-8">
        <div className="text-center text-slate-600">Loading rates...</div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8" data-testid="rates-page">
      <div className="mb-6">
        <h1 className="text-2xl md:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900">Rates Management</h1>
        <p className="mt-1 md:mt-2 text-base md:text-lg text-slate-700">Manage crafts and billing rate classifications</p>
      </div>

      <Tabs defaultValue="crafts" className="w-full">
        <TabsList className="mb-4 md:mb-6 bg-slate-100 p-1 rounded-sm flex flex-wrap gap-1 h-auto">
          <TabsTrigger value="crafts" className="rounded-sm data-[state=active]:bg-white text-xs md:text-sm py-2 px-2 md:px-3">
            <Wrench className="w-4 h-4 mr-1 md:mr-2" />
            Crafts
          </TabsTrigger>
          <TabsTrigger value="classifications" className="rounded-sm data-[state=active]:bg-white text-xs md:text-sm py-2 px-2 md:px-3">
            <DollarSign className="w-4 h-4 mr-1 md:mr-2" />
            <span className="hidden sm:inline">Classifications</span>
            <span className="sm:hidden">Rates</span>
          </TabsTrigger>
          <TabsTrigger value="equipment" className="rounded-sm data-[state=active]:bg-white text-xs md:text-sm py-2 px-2 md:px-3">
            <Truck className="w-4 h-4 mr-1 md:mr-2" />
            <span className="hidden sm:inline">Internal Equipment</span>
            <span className="sm:hidden">Equip.</span>
          </TabsTrigger>
        </TabsList>

        {/* Crafts Tab */}
        <TabsContent value="crafts">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <p className="text-sm text-slate-600">Manage skill categories (e.g., Ironworker, Pipefitter)</p>
            <Button
              onClick={() => handleOpenCraftDialog()}
              data-testid="create-craft-button"
              className="bg-[#1A3668] hover:bg-[#142A52] text-white font-bold rounded-sm py-3 md:py-2 w-full sm:w-auto"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Craft
            </Button>
          </div>

          {crafts.length === 0 ? (
            <Card className="border-slate-200 shadow-sm rounded-sm">
              <CardContent className="p-8 md:p-12 text-center">
                <Wrench className="w-10 md:w-12 h-10 md:h-12 mx-auto text-slate-400 mb-4" />
                <p className="text-slate-600 mb-4">No crafts yet</p>
                <Button
                  onClick={() => handleOpenCraftDialog()}
                  variant="outline"
                  className="border-2 border-[#1A3668] text-[#1A3668] hover:bg-slate-50 rounded-sm py-3"
                >
                  Create your first craft
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
              {crafts.map((craft) => (
                <Card key={craft.id} data-testid={`craft-card-${craft.id}`} className="border-slate-200 shadow-sm hover:shadow-md hover:border-[#1A3668] transition-all rounded-sm">
                  <CardContent className="p-3 md:p-4">
                    <div className="flex items-center justify-between mb-2 md:mb-3">
                      <Wrench className="w-5 h-5 md:w-6 md:h-6 text-[#1A3668]" />
                    </div>
                    <h3 className="text-base md:text-lg font-bold text-slate-900 mb-2 md:mb-3">{craft.name}</h3>
                    <div className="flex gap-1 md:gap-2">
                      <Button
                        onClick={() => handleOpenCraftDialog(craft)}
                        data-testid={`edit-craft-${craft.id}`}
                        variant="outline"
                        size="sm"
                        className="flex-1 border-2 border-slate-200 hover:border-[#1A3668] rounded-sm text-xs p-1 md:p-2"
                      >
                        <Pencil className="w-3 h-3 mr-1" />
                        <span className="hidden sm:inline">Edit</span>
                      </Button>
                      <Button
                        onClick={() => {
                          setDeletingCraft(craft);
                          setDeleteCraftDialogOpen(true);
                        }}
                        data-testid={`delete-craft-${craft.id}`}
                        variant="outline"
                        size="sm"
                        className="border-2 border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 rounded-sm p-1 md:p-2"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Classifications Tab */}
        <TabsContent value="classifications">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <p className="text-sm text-slate-600">Manage billing rate classifications with shift differentials</p>
            <Button
              onClick={() => handleOpenClassDialog()}
              data-testid="create-classification-button"
              className="bg-[#1A3668] hover:bg-[#142A52] text-white font-bold rounded-sm py-3 md:py-2 w-full sm:w-auto"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Classification
            </Button>
          </div>

          {classifications.length === 0 ? (
            <Card className="border-slate-200 shadow-sm rounded-sm">
              <CardContent className="p-8 md:p-12 text-center">
                <DollarSign className="w-10 md:w-12 h-10 md:h-12 mx-auto text-slate-400 mb-4" />
                <p className="text-slate-600 mb-4">No classifications yet</p>
                <Button
                  onClick={() => handleOpenClassDialog()}
                  variant="outline"
                  className="border-2 border-[#1A3668] text-[#1A3668] hover:bg-slate-50 rounded-sm py-3"
                >
                  Create your first classification
                </Button>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Mobile Card View */}
              <div className="md:hidden space-y-3">
                {classifications.map((classification) => (
                  <Card key={classification.id} data-testid={`classification-card-${classification.id}`} className="border-slate-200 shadow-sm rounded-sm">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <p className="text-sm text-[#1A3668] font-medium">{getCraftName(classification.craftId)}</p>
                          <p className="font-bold text-slate-900">{classification.name}</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-2 bg-slate-50 rounded-sm p-2 mb-3">
                        <div className="text-center">
                          <p className="text-xs text-slate-500">ST</p>
                          <p className="font-mono font-bold">${classification.stRate.toFixed(2)}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-slate-500">OT</p>
                          <p className="font-mono font-bold">${classification.otRate.toFixed(2)}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-slate-500">DT</p>
                          <p className="font-mono font-bold">${classification.dtRate.toFixed(2)}</p>
                        </div>
                      </div>
                      <div className="flex gap-1 flex-wrap mb-3">
                        <Badge variant="outline" className="rounded-sm text-xs bg-yellow-50 border-yellow-200 text-yellow-700">
                          <Sun className="w-3 h-3 mr-1" />
                          {formatShiftDiff(classification, 'DAY')}
                        </Badge>
                        <Badge variant="outline" className="rounded-sm text-xs bg-indigo-50 border-indigo-200 text-indigo-700">
                          <Moon className="w-3 h-3 mr-1" />
                          {formatShiftDiff(classification, 'NIGHT')}
                        </Badge>
                        <Badge variant="outline" className="rounded-sm text-xs bg-orange-50 border-orange-200 text-orange-700">
                          <Clock className="w-3 h-3 mr-1" />
                          {formatShiftDiff(classification, 'SWING')}
                        </Badge>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleOpenClassDialog(classification)}
                          data-testid={`edit-classification-${classification.id}`}
                          variant="outline"
                          size="sm"
                          className="flex-1 border-2 border-slate-200 hover:border-[#1A3668] rounded-sm"
                        >
                          <Pencil className="w-3 h-3 mr-1" />
                          Edit
                        </Button>
                        <Button
                          onClick={() => {
                            setDeletingClass(classification);
                            setDeleteClassDialogOpen(true);
                          }}
                          data-testid={`delete-classification-${classification.id}`}
                          variant="outline"
                          size="sm"
                          className="border-2 border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 rounded-sm"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Desktop Table View */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-slate-100 border-b-2 border-slate-200">
                      <th className="text-left p-4 text-sm font-bold uppercase tracking-wider text-slate-700">Craft</th>
                      <th className="text-left p-4 text-sm font-bold uppercase tracking-wider text-slate-700">Classification</th>
                      <th className="text-right p-4 text-sm font-bold uppercase tracking-wider text-slate-700">ST Rate</th>
                      <th className="text-right p-4 text-sm font-bold uppercase tracking-wider text-slate-700">OT Rate</th>
                      <th className="text-right p-4 text-sm font-bold uppercase tracking-wider text-slate-700">DT Rate</th>
                      <th className="text-center p-4 text-sm font-bold uppercase tracking-wider text-slate-700">Shift Differentials</th>
                      <th className="text-center p-4 text-sm font-bold uppercase tracking-wider text-slate-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {classifications.map((classification) => (
                      <tr key={classification.id} data-testid={`classification-row-${classification.id}`} className="border-b border-slate-200 hover:bg-slate-50">
                        <td className="p-4 text-sm font-medium text-slate-700">{getCraftName(classification.craftId)}</td>
                        <td className="p-4 text-sm font-bold text-slate-900">{classification.name}</td>
                        <td className="p-4 text-right text-sm font-mono text-slate-700">${classification.stRate.toFixed(2)}</td>
                        <td className="p-4 text-right text-sm font-mono text-slate-700">${classification.otRate.toFixed(2)}</td>
                        <td className="p-4 text-right text-sm font-mono text-slate-700">${classification.dtRate.toFixed(2)}</td>
                        <td className="p-4 text-center">
                          <div className="flex gap-1 justify-center flex-wrap">
                            <Badge variant="outline" className="rounded-sm text-xs flex items-center gap-1 bg-yellow-50 border-yellow-200 text-yellow-700">
                              <Sun className="w-3 h-3" />
                              {formatShiftDiff(classification, 'DAY')}
                            </Badge>
                            <Badge variant="outline" className="rounded-sm text-xs flex items-center gap-1 bg-indigo-50 border-indigo-200 text-indigo-700">
                              <Moon className="w-3 h-3" />
                              {formatShiftDiff(classification, 'NIGHT')}
                            </Badge>
                            <Badge variant="outline" className="rounded-sm text-xs flex items-center gap-1 bg-orange-50 border-orange-200 text-orange-700">
                              <Clock className="w-3 h-3" />
                              {formatShiftDiff(classification, 'SWING')}
                            </Badge>
                          </div>
                        </td>
                        <td className="p-4 text-center">
                          <div className="flex gap-2 justify-center">
                            <Button
                              onClick={() => handleOpenClassDialog(classification)}
                              data-testid={`edit-classification-${classification.id}`}
                              variant="outline"
                              size="sm"
                              className="border-2 border-slate-200 hover:border-[#1A3668] rounded-sm"
                            >
                              <Pencil className="w-3 h-3" />
                            </Button>
                            <Button
                              onClick={() => {
                                setDeletingClass(classification);
                                setDeleteClassDialogOpen(true);
                              }}
                              data-testid={`delete-classification-${classification.id}`}
                              variant="outline"
                              size="sm"
                              className="border-2 border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 rounded-sm"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </TabsContent>

        {/* Internal Equipment Tab */}
        <TabsContent value="equipment">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <p className="text-sm text-slate-600">Manage company-owned equipment charged to jobs (trucks, welders, generators, etc.)</p>
            <Button
              onClick={() => openEquipDialog()}
              data-testid="create-equipment-button"
              className="bg-[#1A3668] hover:bg-[#142A52] text-white font-bold rounded-sm py-3 md:py-2 w-full sm:w-auto"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Equipment
            </Button>
          </div>

          {internalEquipment.length === 0 ? (
            <Card className="border-slate-200 shadow-sm rounded-sm">
              <CardContent className="p-8 md:p-12 text-center">
                <Truck className="w-10 md:w-12 h-10 md:h-12 mx-auto text-slate-400 mb-4" />
                <p className="text-slate-600 mb-4">No internal equipment defined yet</p>
                <Button
                  onClick={() => openEquipDialog()}
                  className="bg-[#1A3668] hover:bg-[#142A52] text-white font-bold rounded-sm py-3"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Your First Equipment
                </Button>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Mobile Card View */}
              <div className="md:hidden space-y-3">
                {internalEquipment.map((equip) => (
                  <Card key={equip.id} className="border-slate-200 shadow-sm rounded-sm">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <p className="font-bold text-slate-900">{equip.name}</p>
                          <p className="text-sm text-slate-600">{equip.description || '-'}</p>
                        </div>
                        <Badge className={equip.active !== false ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-600'}>
                          {equip.active !== false ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                      <p className="font-mono font-bold text-[#1A3668] mb-3">
                        ${equip.hourlyRate.toFixed(2)}/hr
                      </p>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => openEquipDialog(equip)}
                          data-testid={`edit-equipment-${equip.id}`}
                          variant="outline"
                          size="sm"
                          className="flex-1 border-2 border-slate-200 hover:border-[#1A3668] rounded-sm"
                        >
                          <Pencil className="w-3 h-3 mr-1" />
                          Edit
                        </Button>
                        <Button
                          onClick={() => {
                            setDeletingEquip(equip);
                            setDeleteEquipDialogOpen(true);
                          }}
                          data-testid={`delete-equipment-${equip.id}`}
                          variant="outline"
                          size="sm"
                          className="border-2 border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 rounded-sm"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Desktop Table View */}
              <div className="hidden md:block bg-white border border-slate-200 rounded-sm overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left p-4 text-sm font-bold uppercase tracking-wider text-slate-500">Name</th>
                      <th className="text-left p-4 text-sm font-bold uppercase tracking-wider text-slate-500">Description</th>
                      <th className="text-right p-4 text-sm font-bold uppercase tracking-wider text-slate-500">Hourly Rate</th>
                      <th className="text-center p-4 text-sm font-bold uppercase tracking-wider text-slate-500">Status</th>
                      <th className="text-right p-4 text-sm font-bold uppercase tracking-wider text-slate-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {internalEquipment.map((equip, index) => (
                      <tr key={equip.id} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                        <td className="p-4 font-bold text-slate-900">{equip.name}</td>
                        <td className="p-4 text-slate-600">{equip.description || '-'}</td>
                        <td className="p-4 text-right font-mono font-bold text-[#1A3668]">
                          ${equip.hourlyRate.toFixed(2)}/hr
                        </td>
                        <td className="p-4 text-center">
                          <Badge className={equip.active !== false ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-600'}>
                            {equip.active !== false ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td className="p-4 text-right">
                          <div className="flex gap-2 justify-end">
                            <Button
                              onClick={() => openEquipDialog(equip)}
                              data-testid={`edit-equipment-${equip.id}`}
                              variant="outline"
                              size="sm"
                              className="border-2 border-slate-200 hover:border-[#1A3668] rounded-sm"
                            >
                              <Pencil className="w-3 h-3" />
                            </Button>
                            <Button
                              onClick={() => {
                                setDeletingEquip(equip);
                                setDeleteEquipDialogOpen(true);
                              }}
                              data-testid={`delete-equipment-${equip.id}`}
                              variant="outline"
                              size="sm"
                              className="border-2 border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 rounded-sm"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Craft Create/Edit Dialog */}
      <Dialog open={craftDialogOpen} onOpenChange={setCraftDialogOpen}>
        <DialogContent className="sm:max-w-[500px] rounded-sm">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">
              {editingCraft ? 'Edit Craft' : 'New Craft'}
            </DialogTitle>
            <DialogDescription>
              {editingCraft ? 'Update craft information' : 'Add a new skill category'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCraftSubmit}>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="craftName" className="text-sm font-bold uppercase tracking-wider text-slate-500">
                  Craft Name *
                </Label>
                <Input
                  id="craftName"
                  data-testid="craft-name-input"
                  value={craftFormData.name}
                  onChange={(e) => setCraftFormData({ name: e.target.value })}
                  placeholder="Ironworker"
                  required
                  className="mt-2 rounded-sm border-2"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setCraftDialogOpen(false)}
                className="border-2 border-slate-200 rounded-sm"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                data-testid="submit-craft-button"
                disabled={craftSubmitting}
                className="bg-[#1A3668] hover:bg-[#142A52] text-white font-bold rounded-sm"
              >
                {craftSubmitting ? 'Saving...' : editingCraft ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Classification Create/Edit Dialog */}
      <Dialog open={classDialogOpen} onOpenChange={setClassDialogOpen}>
        <DialogContent className="sm:max-w-[700px] rounded-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">
              {editingClass ? 'Edit Classification' : 'New Classification'}
            </DialogTitle>
            <DialogDescription>
              {editingClass ? 'Update classification, billing rates, and shift differentials' : 'Add a new billing rate classification with shift multipliers'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleClassSubmit}>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="craftId" className="text-sm font-bold uppercase tracking-wider text-slate-500">
                  Craft *
                </Label>
                <Select
                  value={classFormData.craftId}
                  onValueChange={(value) => setClassFormData({ ...classFormData, craftId: value })}
                  required
                >
                  <SelectTrigger data-testid="classification-craft-select" className="mt-2 rounded-sm border-2">
                    <SelectValue placeholder="Select craft" />
                  </SelectTrigger>
                  <SelectContent>
                    {crafts.map((craft) => (
                      <SelectItem key={craft.id} value={craft.id}>
                        {craft.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="className" className="text-sm font-bold uppercase tracking-wider text-slate-500">
                  Classification Name *
                </Label>
                <Input
                  id="className"
                  data-testid="classification-name-input"
                  value={classFormData.name}
                  onChange={(e) => setClassFormData({ ...classFormData, name: e.target.value })}
                  placeholder="Journeyman"
                  required
                  className="mt-2 rounded-sm border-2"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="stRate" className="text-sm font-bold uppercase tracking-wider text-slate-500">
                    ST Rate *
                  </Label>
                  <Input
                    id="stRate"
                    data-testid="st-rate-input"
                    type="number"
                    step="0.01"
                    value={classFormData.stRate}
                    onChange={(e) => setClassFormData({ ...classFormData, stRate: e.target.value })}
                    placeholder="65.00"
                    required
                    className="mt-2 rounded-sm border-2"
                  />
                </div>
                <div>
                  <Label htmlFor="otRate" className="text-sm font-bold uppercase tracking-wider text-slate-500">
                    OT Rate *
                  </Label>
                  <Input
                    id="otRate"
                    data-testid="ot-rate-input"
                    type="number"
                    step="0.01"
                    value={classFormData.otRate}
                    onChange={(e) => setClassFormData({ ...classFormData, otRate: e.target.value })}
                    placeholder="97.50"
                    required
                    className="mt-2 rounded-sm border-2"
                  />
                </div>
                <div>
                  <Label htmlFor="dtRate" className="text-sm font-bold uppercase tracking-wider text-slate-500">
                    DT Rate *
                  </Label>
                  <Input
                    id="dtRate"
                    data-testid="dt-rate-input"
                    type="number"
                    step="0.01"
                    value={classFormData.dtRate}
                    onChange={(e) => setClassFormData({ ...classFormData, dtRate: e.target.value })}
                    placeholder="130.00"
                    required
                    className="mt-2 rounded-sm border-2"
                  />
                </div>
              </div>

              {/* Shift Differentials Section */}
              <div className="pt-4 border-t border-slate-200">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-sm font-bold uppercase tracking-wider text-slate-500">
                    Shift Differentials
                  </Label>
                  <div className="flex gap-1 bg-slate-100 p-1 rounded-sm">
                    <Button
                      type="button"
                      size="sm"
                      variant={classFormData.shiftDiffType === 'MULTIPLIER' ? 'default' : 'ghost'}
                      onClick={() => setClassFormData({ ...classFormData, shiftDiffType: 'MULTIPLIER' })}
                      className={`text-xs rounded-sm px-3 py-1 h-auto ${classFormData.shiftDiffType === 'MULTIPLIER' ? 'bg-[#1A3668]' : ''}`}
                    >
                      Multiplier
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={classFormData.shiftDiffType === 'FLAT' ? 'default' : 'ghost'}
                      onClick={() => setClassFormData({ ...classFormData, shiftDiffType: 'FLAT' })}
                      className={`text-xs rounded-sm px-3 py-1 h-auto ${classFormData.shiftDiffType === 'FLAT' ? 'bg-[#1A3668]' : ''}`}
                    >
                      Flat $/Hour
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-slate-500 mb-3">
                  {classFormData.shiftDiffType === 'MULTIPLIER' 
                    ? 'Set multipliers for each shift. 1.0 = no differential, 1.15 = 15% premium.'
                    : 'Set flat hourly amounts added to base rate for each shift (e.g., $2.00/hr extra for night).'}
                </p>
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-yellow-50 p-3 rounded-sm border border-yellow-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Sun className="w-4 h-4 text-yellow-600" />
                      <Label htmlFor="dayDiff" className="text-sm font-bold text-yellow-700">Day Shift</Label>
                    </div>
                    {classFormData.shiftDiffType === 'MULTIPLIER' ? (
                      <Input
                        id="dayDiff"
                        data-testid="day-diff-input"
                        type="number"
                        step="0.01"
                        min="0.5"
                        max="3.0"
                        value={classFormData.shiftDiffs?.DAY || 1.0}
                        onChange={(e) => setClassFormData({
                          ...classFormData,
                          shiftDiffs: { ...classFormData.shiftDiffs, DAY: e.target.value }
                        })}
                        placeholder="1.00"
                        className="rounded-sm border-yellow-200"
                      />
                    ) : (
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                        <Input
                          id="dayDiffFlat"
                          data-testid="day-diff-flat-input"
                          type="number"
                          step="0.01"
                          min="0"
                          value={classFormData.shiftDiffsFlat?.DAY || 0}
                          onChange={(e) => setClassFormData({
                            ...classFormData,
                            shiftDiffsFlat: { ...classFormData.shiftDiffsFlat, DAY: e.target.value }
                          })}
                          placeholder="0.00"
                          className="rounded-sm border-yellow-200 pl-7"
                        />
                      </div>
                    )}
                  </div>
                  <div className="bg-indigo-50 p-3 rounded-sm border border-indigo-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Moon className="w-4 h-4 text-indigo-600" />
                      <Label htmlFor="nightDiff" className="text-sm font-bold text-indigo-700">Night Shift</Label>
                    </div>
                    {classFormData.shiftDiffType === 'MULTIPLIER' ? (
                      <Input
                        id="nightDiff"
                        data-testid="night-diff-input"
                        type="number"
                        step="0.01"
                        min="0.5"
                        max="3.0"
                        value={classFormData.shiftDiffs?.NIGHT || 1.0}
                        onChange={(e) => setClassFormData({
                          ...classFormData,
                          shiftDiffs: { ...classFormData.shiftDiffs, NIGHT: e.target.value }
                        })}
                        placeholder="1.15"
                        className="rounded-sm border-indigo-200"
                      />
                    ) : (
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                        <Input
                          id="nightDiffFlat"
                          data-testid="night-diff-flat-input"
                          type="number"
                          step="0.01"
                          min="0"
                          value={classFormData.shiftDiffsFlat?.NIGHT || 0}
                          onChange={(e) => setClassFormData({
                            ...classFormData,
                            shiftDiffsFlat: { ...classFormData.shiftDiffsFlat, NIGHT: e.target.value }
                          })}
                          placeholder="2.00"
                          className="rounded-sm border-indigo-200 pl-7"
                        />
                      </div>
                    )}
                  </div>
                  <div className="bg-orange-50 p-3 rounded-sm border border-orange-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="w-4 h-4 text-orange-600" />
                      <Label htmlFor="swingDiff" className="text-sm font-bold text-orange-700">Swing Shift</Label>
                    </div>
                    {classFormData.shiftDiffType === 'MULTIPLIER' ? (
                      <Input
                        id="swingDiff"
                        data-testid="swing-diff-input"
                        type="number"
                        step="0.01"
                        min="0.5"
                        max="3.0"
                        value={classFormData.shiftDiffs?.SWING || 1.0}
                        onChange={(e) => setClassFormData({
                          ...classFormData,
                          shiftDiffs: { ...classFormData.shiftDiffs, SWING: e.target.value }
                        })}
                        placeholder="1.10"
                        className="rounded-sm border-orange-200"
                      />
                    ) : (
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                        <Input
                          id="swingDiffFlat"
                          data-testid="swing-diff-flat-input"
                          type="number"
                          step="0.01"
                          min="0"
                          value={classFormData.shiftDiffsFlat?.SWING || 0}
                          onChange={(e) => setClassFormData({
                            ...classFormData,
                            shiftDiffsFlat: { ...classFormData.shiftDiffsFlat, SWING: e.target.value }
                          })}
                          placeholder="1.50"
                          className="rounded-sm border-orange-200 pl-7"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setClassDialogOpen(false)}
                className="border-2 border-slate-200 rounded-sm"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                data-testid="submit-classification-button"
                disabled={classSubmitting}
                className="bg-[#1A3668] hover:bg-[#142A52] text-white font-bold rounded-sm"
              >
                {classSubmitting ? 'Saving...' : editingClass ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Craft Dialog */}
      <AlertDialog open={deleteCraftDialogOpen} onOpenChange={setDeleteCraftDialogOpen}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Craft</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{deletingCraft?.name}</strong>? This may affect crew members and classifications.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteCraft}
              data-testid="confirm-delete-craft"
              className="bg-red-600 hover:bg-red-700 rounded-sm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Classification Dialog */}
      <AlertDialog open={deleteClassDialogOpen} onOpenChange={setDeleteClassDialogOpen}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Classification</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{deletingClass?.name}</strong>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteClass}
              data-testid="confirm-delete-classification"
              className="bg-red-600 hover:bg-red-700 rounded-sm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Internal Equipment Create/Edit Dialog */}
      <Dialog open={equipDialogOpen} onOpenChange={setEquipDialogOpen}>
        <DialogContent className="sm:max-w-[500px] rounded-sm">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold flex items-center gap-2">
              <Truck className="w-6 h-6 text-[#1A3668]" />
              {editingEquip ? 'Edit Equipment' : 'New Internal Equipment'}
            </DialogTitle>
            <DialogDescription>
              {editingEquip ? 'Update equipment information' : 'Add company-owned equipment to charge to jobs'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEquipSubmit}>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="equipName" className="text-sm font-bold uppercase tracking-wider text-slate-500">
                  Equipment Name *
                </Label>
                <Input
                  id="equipName"
                  data-testid="equipment-name-input"
                  value={equipFormData.name}
                  onChange={(e) => setEquipFormData({ ...equipFormData, name: e.target.value })}
                  placeholder="Truck #1, Welder, Generator"
                  required
                  className="mt-2 rounded-sm border-2"
                />
              </div>
              <div>
                <Label htmlFor="equipDescription" className="text-sm font-bold uppercase tracking-wider text-slate-500">
                  Description
                </Label>
                <Input
                  id="equipDescription"
                  data-testid="equipment-description-input"
                  value={equipFormData.description}
                  onChange={(e) => setEquipFormData({ ...equipFormData, description: e.target.value })}
                  placeholder="2024 Ford F-250, Lincoln Welder 250, etc."
                  className="mt-2 rounded-sm border-2"
                />
              </div>
              <div>
                <Label htmlFor="equipRate" className="text-sm font-bold uppercase tracking-wider text-slate-500">
                  Hourly Rate ($) *
                </Label>
                <Input
                  id="equipRate"
                  data-testid="equipment-rate-input"
                  type="number"
                  step="0.01"
                  min="0"
                  value={equipFormData.hourlyRate}
                  onChange={(e) => setEquipFormData({ ...equipFormData, hourlyRate: e.target.value })}
                  placeholder="75.00"
                  required
                  className="mt-2 rounded-sm border-2"
                />
                <p className="text-xs text-slate-500 mt-1">Rate charged per hour when this equipment is used on a job</p>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setEquipDialogOpen(false)}
                className="border-2 border-slate-200 rounded-sm"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={equipSubmitting}
                data-testid="save-equipment-button"
                className="bg-[#1A3668] hover:bg-[#142A52] text-white font-bold rounded-sm"
              >
                {equipSubmitting ? 'Saving...' : editingEquip ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Equipment Dialog */}
      <AlertDialog open={deleteEquipDialogOpen} onOpenChange={setDeleteEquipDialogOpen}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Equipment</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{deletingEquip?.name}</strong>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteEquip}
              data-testid="confirm-delete-equipment"
              className="bg-red-600 hover:bg-red-700 rounded-sm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
